import { useState } from 'react';
import { CatalogueListLayout } from '@/components/common/CatalogueListLayout';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { DataTable } from '@/components/common/DataTable';
import { ErrorState } from '@/components/common/ErrorState';
import { useInfiniteTableQuery } from '@/hooks/useInfiniteTableQuery';
import { promoItemTypesInfiniteQueryOptions } from '../api/promotion.api';
import { getPromoItemTypeColumns } from '../components/PromoColumns';
import { PromoItemTypeFormModal } from '../components/PromoFormModals';
import { usePromoCatalogueFilters } from '../hooks/usePromoCatalogueFilters';
import { useDeletePromoItemType } from '../hooks/usePromoMutations';
import type { PromoItemType } from '../types/promotion.types';
import { getPromoItemTypeStatCards } from '../utils/promotionStatCards';

export function PromoItemTypePage() {
  const controller = usePromoCatalogueFilters('itemTypes');
  const { rows, firstPageMeta, isLoading, isRefetching, loadError, refetch, loadMoreProps } =
    useInfiniteTableQuery(promoItemTypesInfiniteQueryOptions(controller.params));

  const [editing, setEditing] = useState<PromoItemType | null>(null);
  const [isFormOpen, setFormOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<PromoItemType | null>(null);

  const remove = useDeletePromoItemType(() => setPendingDelete(null));

  const columns = getPromoItemTypeColumns(controller.search.search, {
    onEdit: (row) => {
      setEditing(row);
      setFormOpen(true);
    },
    onDelete: setPendingDelete,
  });

  return (
    <CatalogueListLayout
      title="Promotional Item Type"
      search={{
        value: controller.searchText,
        onChange: controller.setSearchText,
        placeholder: 'Search item types',
      }}
      filters={controller}
      stats={{
        cards: getPromoItemTypeStatCards(firstPageMeta),
        isLoading,
        isFetching: isRefetching,
        isError: loadError !== null,
      }}
      onAdd={() => {
        setEditing(null);
        setFormOpen(true);
      }}
      addLabel="Add Item Type"
    >
      {loadError ? (
        <ErrorState
          error={loadError}
          fallbackTitle="Could not load the item types"
          onRetry={() => void refetch()}
          fill
        />
      ) : (
        <DataTable
          data={rows}
          columns={columns}
          isLoading={isLoading}
          className="lg:flex-1"
          stickyColumnId="name"
          emptyMessage="No item type matches"
          {...loadMoreProps}
        />
      )}

      <PromoItemTypeFormModal itemType={editing} open={isFormOpen} onOpenChange={setFormOpen} />

      <ConfirmDialog
        open={pendingDelete !== null}
        onOpenChange={(open) => !open && setPendingDelete(null)}
        title="Remove this item type?"
        description={
          pendingDelete === null
            ? ''
            : `"${pendingDelete.name}" will no longer be offered. A type ${pendingDelete.itemCount} items use cannot be removed.`
        }
        confirmLabel="Remove"
        confirmVariant="destructive"
        isPending={remove.isPending}
        onConfirm={() => pendingDelete && remove.mutate(pendingDelete.id)}
      />
    </CatalogueListLayout>
  );
}
